import { supabase } from '@/lib/supabase';

/**
 * Debug utility to check camp_audit_data table schema
 */
export const checkCampAuditDataSchema = async () => {
  console.group('🔍 Checking camp_audit_data Schema');
  
  try {
    // Try to select from the table
    const { data, error } = await supabase
      .from('camp_audit_data')
      .select('*')
      .limit(1);
    
    if (error) {
      console.error('❌ Error accessing camp_audit_data:', error);
      console.error('Error code:', error.code);
      console.error('Error details:', error.details);
      console.error('Error hint:', error.hint);
      
      if (error.code === '42P01') {
        console.error('🚨 TABLE DOES NOT EXIST!');
        console.log('📝 You need to run the database schema SQL:');
        console.log('   File: database_schema_caching.sql');
      }
      
      console.groupEnd();
      return { exists: false, error };
    }
    
    if (data && data.length > 0) {
      console.log('✅ Table exists and has data');
      console.log('📊 Sample record:', data[0]);
      console.log('📋 Available columns:', Object.keys(data[0]));
    } else {
      console.log('✅ Table exists but is empty');
      console.log('📝 Ready to insert data');
    }
    
    console.groupEnd();
    return { exists: true, hasData: data && data.length > 0, columns: data && data.length > 0 ? Object.keys(data[0]) : [] };
    
  } catch (error) {
    console.error('❌ Unexpected error:', error);
    console.groupEnd();
    return { exists: false, error };
  }
};

/**
 * Test basic insert to identify schema issues
 */
export const testBasicInsert = async () => {
  console.group('🧪 Testing Basic Insert');
  
  try {
    const testData = {
      audit_id: `TEST-${Date.now()}`,
      name: 'Test Camp',
      location: 'Test Location',
      audit_date: new Date().toISOString(),
      audit_year: new Date().getFullYear(),
      audit_week: 1,
      api_compliant: true,
      ethical_source: true
    };
    
    console.log('📝 Attempting to insert:', testData);
    
    const { data, error } = await supabase
      .from('camp_audit_data')
      .insert(testData)
      .select();
    
    if (error) {
      console.error('❌ Insert failed:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Error details:', error.details);
      console.error('Error hint:', error.hint);
      
      if (error.code === '23502') {
        console.error('🚨 MISSING REQUIRED COLUMN!');
        console.error('The error details will tell you which column is required');
      }
      
      if (error.code === '42703') {
        console.error('🚨 COLUMN DOES NOT EXIST!');
        console.error('The column you are trying to insert does not exist in the table');
      }
      
      console.groupEnd();
      return { success: false, error };
    }
    
    console.log('✅ Insert successful!');
    console.log('📊 Inserted record:', data[0]);
    
    // Clean up test record
    await supabase
      .from('camp_audit_data')
      .delete()
      .eq('audit_id', testData.audit_id);
    
    console.log('🧹 Test record cleaned up');
    console.groupEnd();
    return { success: true, data: data[0] };
    
  } catch (error) {
    console.error('❌ Unexpected error:', error);
    console.groupEnd();
    return { success: false, error };
  }
};

/**
 * Get required columns for camp_audit_data
 */
export const getRequiredColumns = () => {
  return [
    'audit_id',
    'name',
    'location',
    'audit_date',
    'audit_year',
    'audit_week',
    'api_compliant',
    'ethical_source'
  ];
};

/**
 * Get optional Google Places columns
 */
export const getGooglePlacesColumns = () => {
  return [
    'place_id',
    'google_types',
    'formatted_address',
    'coordinates_lat',
    'coordinates_lng',
    'user_ratings_total',
    'price_level',
    'business_status',
    'editorial_summary',
    'photos',
    'photo_count',
    'reviews',
    'review_summary',
    'primary_category',
    'all_categories',
    'category_confidence',
    'keywords_matched',
    'last_fetched_from_google',
    'data_freshness_days',
    'needs_refresh',
    'view_count',
    'save_count',
    'click_count',
    'popularity_score',
    'search_keywords',
    'last_search_date'
  ];
};
